import dayjs from 'dayjs';

export interface WorkSchedule {
  weeks: string[][];
  nonWorkingWeeks?: boolean[];
}

/**
 * A schedule day is considered "zero" when it is empty or resolves to 00:00,
 * i.e. no hours/minutes have been entered for that day.
 */
const isZeroDuration = (value: string): boolean => {
  const [hours, minutes] = (value ?? '').split(':').map(Number);
  return (
    (Number.isNaN(hours) ? 0 : hours) === 0 &&
    (Number.isNaN(minutes) ? 0 : minutes) === 0
  );
};

/**
 * Returns true when the given date falls on a non-working day according to
 * the provided work schedule.
 *
 * Shared between the `dateValidWorkDay` validator (src/components/forms/validators)
 * and the `dateIsNonWorkDay` showWhen checker (src/components/forms/options) so
 * the "is this a non-working day" logic lives in a single place.
 */
export const isNonWorkDay = (
  dateValue: string | Date,
  schedule: WorkSchedule,
): boolean => {
  const weeks = schedule.weeks;
  const nonWorkingWeeks: boolean[] = Array.isArray(schedule.nonWorkingWeeks)
    ? schedule.nonWorkingWeeks
    : [];

  // weeks[0] always represents the current week (the week containing today).
  // For rotational schedules, offset tells us how many weeks ahead the selected
  // date is; we cycle back through the rotation with modulo.
  const n = weeks.length;
  const weekOffset = dayjs(dateValue)
    .startOf('week')
    .diff(dayjs().startOf('week'), 'week');
  // JS % can return negative values, so normalise to [0, n).
  const weekIndex = ((weekOffset % n) + n) % n;

  if (nonWorkingWeeks[weekIndex]) {
    return true;
  }

  const selectedDay = dayjs(dateValue).day();
  return isZeroDuration(weeks[weekIndex][selectedDay]);
};
